import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from '../../../../icons';
import './styles.scss';

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => i);

function CalendarPicker({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  required,
  minDate,
  maxDate,
  placeholder,
  error,
  showTime,
  dataTestPrefix,
  onConfirm,
  onCancel,
  caption,
  extra,
}) {
  const dt = (suffix) => (dataTestPrefix ? `${dataTestPrefix}-${suffix}` : undefined);
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  const snapshotRef = useRef(null);

  const parsed = parseIso(value, showTime);
  const today = todayParts();
  const normalizedMinDate = useMemo(() => normalizeDateOnly(minDate), [minDate]);
  const normalizedMaxDate = useMemo(() => normalizeDateOnly(maxDate), [maxDate]);

  const [viewYear, setViewYear] = useState(parsed?.year ?? today.year);
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.month);
  const [hour12, setHour12] = useState(() => to12Hour(parsed?.hour ?? today.hour).hour12);
  const [minute, setMinute] = useState(parsed?.minute ?? today.minute);
  const [meridiem, setMeridiem] = useState(() => to12Hour(parsed?.hour ?? today.hour).meridiem);

  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.year);
      setViewMonth(parsed.month);
      if (showTime) {
        const { hour12: h12, meridiem: mer } = to12Hour(parsed.hour ?? 0);
        setHour12(h12);
        setMeridiem(mer);
        setMinute(parsed.minute ?? 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    snapshotRef.current = value ?? null;
    if (!parsed) {
      const now = todayParts();
      const { hour12: h12, meridiem: mer } = to12Hour(now.hour);
      setViewYear(now.year);
      setViewMonth(now.month);
      setHour12(h12);
      setMinute(now.minute);
      setMeridiem(mer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    function handleOutside(e) {
      const contains = wrapRef.current && wrapRef.current.contains(e.target);
      if (wrapRef.current && !contains) {
        setIsOpen(false);
        onBlur?.();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }
  }, [isOpen, onBlur]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function currentHour24() {
    return to24Hour(hour12, meridiem);
  }

  function emitChange(year, month, day, hour, minuteVal) {
    const iso = toIso(year, month, day, hour, minuteVal, showTime);
    onChange(iso);
    setTextValue(formatDisplay({ year, month, day, hour, minute: minuteVal }, showTime));
  }

  function handleDayClick(day) {
    const hour = showTime ? currentHour24() : undefined;
    const minuteVal = showTime ? minute : undefined;
    const iso = toIso(viewYear, viewMonth, day, hour, minuteVal, showTime);
    const isoDateOnly = toIso(viewYear, viewMonth, day);
    if (normalizedMinDate && isoDateOnly < normalizedMinDate) return;
    if (normalizedMaxDate && isoDateOnly > normalizedMaxDate) return;
    isEditingRef.current = false;
    onChange(iso);
    setTextValue(formatDisplay({ year: viewYear, month: viewMonth, day, hour, minute: minuteVal }, showTime));
    if (!showTime) {
      setIsOpen(false);
      onBlur?.();
    }
  }

  function handleDone() {
    isEditingRef.current = false;
    if (!parsed) {
      const now = todayParts();
      const todayIsoDate = toIso(now.year, now.month, now.day);
      const withinRange = !(normalizedMinDate && todayIsoDate < normalizedMinDate) && !(normalizedMaxDate && todayIsoDate > normalizedMaxDate);
      if (withinRange) emitChange(now.year, now.month, now.day, currentHour24(), minute);
    }
    setIsOpen(false);
    onBlur?.();
  }

  function handleCancel() {
    isEditingRef.current = false;
    if (snapshotRef.current !== (value ?? null)) onChange(snapshotRef.current);
    setIsOpen(false);
    onBlur?.();
  }

  function handleTimeChange(nextHour12, nextMinute, nextMeridiem) {
    if (!parsed) return;
    const hour24 = to24Hour(nextHour12, nextMeridiem);
    emitChange(viewYear, viewMonth, parsed.day, hour24, nextMinute);
  }

  function onHourChange(e) {
    const nextHour12 = Number(e.target.value);
    setHour12(nextHour12);
    handleTimeChange(nextHour12, minute, meridiem);
  }

  function onMinuteChange(e) {
    const nextMinute = Number(e.target.value);
    setMinute(nextMinute);
    handleTimeChange(hour12, nextMinute, meridiem);
  }

  function onMeridiemChange(e) {
    const nextMeridiem = e.target.value;
    setMeridiem(nextMeridiem);
    handleTimeChange(hour12, minute, nextMeridiem);
  }

  function toggleOpen() {
    if (disabled) return;
    setIsOpen(o => !o);
  }

  const calWeeks = useMemo(() => {
    const days = buildCalendarDays(viewYear, viewMonth);
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
  }, [viewYear, viewMonth]);
  const todayIsoValue = toIso(today.year, today.month, today.day);

  const currentYear = today.year;
  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear - 80; y <= currentYear + 20; y++) arr.push(y);
    return arr;
  }, [currentYear]);

  const displayValue = parsed ? formatDisplay(parsed, showTime) : '';

  const [textValue, setTextValue] = useState(displayValue);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) setTextValue(displayValue);
  }, [displayValue]);

  function commitTyped() {
    isEditingRef.current = false;

    if (!textValue.trim()) {
      if (displayValue) onChange(null);
      setTextValue('');
      return;
    }

    const typed = parseTyped(textValue, showTime);
    if (!typed) {
      setTextValue(displayValue);
      return;
    }

    const isoDateOnly = toIso(typed.year, typed.month, typed.day);
    if ((normalizedMinDate && isoDateOnly < normalizedMinDate) || (normalizedMaxDate && isoDateOnly > normalizedMaxDate)) {
      setTextValue(displayValue);
      return;
    }

    setViewYear(typed.year);
    setViewMonth(typed.month);
    if (showTime) {
      const { hour12: h12, meridiem: mer } = to12Hour(typed.hour ?? 0);
      setHour12(h12);
      setMeridiem(mer);
      setMinute(typed.minute ?? 0);
    }
    const iso = toIso(typed.year, typed.month, typed.day, typed.hour, typed.minute, showTime);
    onChange(iso);
    setTextValue(formatDisplay(typed, showTime));
  }

  const pickerNode = (
    <div ref={wrapRef} className={`field-engine-datepicker${disabled ? ' disabled' : ''}`}>
      <div className="field-engine-datepicker-row">
        <input
          id={id}
          data-test={dt('input')}
          type="text"
          className={`field-engine-input field-engine-datepicker-text${error ? ' field-engine-input--error' : ''}${disabled ? ' disabled' : ''}`}
          value={textValue}
          placeholder={placeholder ?? 'Select Date'}
          disabled={disabled}
          required={required}
          onFocus={() => {
            isEditingRef.current = true;
            if (!disabled) setIsOpen(true);
          }}
          onChange={(e) => setTextValue(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTyped();
              setIsOpen(false);
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-required={required}
          aria-invalid={!!error}
        />
        <button
          type="button"
          data-test={dt('icon')}
          className="field-engine-datepicker-icon-btn"
          onClick={toggleOpen}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Open date picker"
        >
          <CalendarIcon />
        </button>
      </div>

      {isOpen && !disabled && (
        <div className="ex-cal" role="dialog" aria-label="Date picker" data-test={dt('calendar')}>
          <div className="ex-cal__panel">
            <div className="ex-cal__header">
              <button type="button" data-test={dt('prev')} className="ex-cal__nav ex-cal__nav--prev" onClick={prevMonth} aria-label="Previous month" />
              <button type="button" data-test={dt('next')} className="ex-cal__nav ex-cal__nav--next" onClick={nextMonth} aria-label="Next month" />
              <div className="ex-cal__title">{MONTHS_FULL[viewMonth]} {viewYear}</div>
              <div className="ex-cal__selects">
                <select
                  data-test={dt('month')}
                  className="ex-cal__select ex-cal__select--month"
                  value={viewMonth}
                  onChange={e => setViewMonth(Number(e.target.value))}
                >
                  {MONTHS_FULL.map((m, i) => (
                    <option key={m} value={i}>{m}</option>
                  ))}
                </select>
                <select
                  data-test={dt('year')}
                  className="ex-cal__select ex-cal__select--year"
                  value={viewYear}
                  onChange={e => setViewYear(Number(e.target.value))}
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="ex-cal__dow-row">
                {DOW.map(d => (
                  <div key={d} className="ex-cal__dow">{d}</div>
                ))}
              </div>
            </div>

            <div className="ex-cal__month">
              {calWeeks.map((week, weekIdx) => (
                <div key={weekIdx} className="ex-cal__week">
                  {week.map((day, dayIdx) => {
                    if (day === null) {
                      return <div key={`e${weekIdx}-${dayIdx}`} className="ex-cal__day ex-cal__day--empty" />;
                    }
                    const isoDateOnly = toIso(viewYear, viewMonth, day);
                    const isToday = isoDateOnly === todayIsoValue;
                    const isDisabled = (normalizedMinDate && isoDateOnly < normalizedMinDate) || (normalizedMaxDate && isoDateOnly > normalizedMaxDate);
                    const isSelected = parsed
                      ? parsed.year === viewYear && parsed.month === viewMonth && parsed.day === day
                      : isToday && !isDisabled;

                    return (
                      <div
                        key={isoDateOnly}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-pressed={isSelected}
                        aria-disabled={isDisabled}
                        className={[
                          'ex-cal__day',
                          isToday ? 'ex-cal__day--today' : '',
                          isSelected ? 'ex-cal__day--selected' : '',
                          isDisabled ? 'ex-cal__day--disabled' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => !isDisabled && handleDayClick(day)}
                        onKeyDown={e => {
                          if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                            handleDayClick(day);
                          }
                        }}
                      >
                        {day}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {showTime && (
            <>
              <div className="ex-cal__time">
                <label className="ex-cal__time-label">Time</label>
                <select data-test={dt('hour')} className="ex-cal__select" value={hour12} onChange={onHourChange}>
                  {HOURS_12.map(h => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                  ))}
                </select>
                <select data-test={dt('minute')} className="ex-cal__select" value={minute} onChange={onMinuteChange}>
                  {MINUTES_60.map(m => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <select data-test={dt('meridiem')} className="ex-cal__select" value={meridiem} onChange={onMeridiemChange}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <div className="ex-cal__actions">
                <button type="button" className="ex-cal__action ex-cal__action--cancel" onClick={handleCancel}>Cancel</button>
                <button type="button" className="ex-cal__action ex-cal__action--done" onClick={handleDone}>Done</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (!onConfirm && !onCancel && !caption && !extra) return pickerNode;

  const actionsNode = (onConfirm || onCancel) ? (
    <div className="carrierCheck rowSpaceBetween" style={{ float: 'none', flexShrink: 0 }}>
      {onConfirm && (
        <span className="statusCheck mr__1" onClick={onConfirm}>
          <Icon name="check" className="icon activeIcon check" />
        </span>
      )}
      {onCancel && (
        <span onClick={onCancel}>
          <Icon name="close" className="icon close" />
        </span>
      )}
    </div>
  ) : null;

  if (extra) {
    return (
      <div>
        {pickerNode}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{extra}</div>
          {actionsNode}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>{pickerNode}</div>
        {actionsNode}
      </div>
      {caption && <div className="field-engine-datepicker-caption" style={{ marginTop: '4px', fontSize: '12px', color: '#8a8a8a' }}>{caption}</div>}
    </div>
  );
}

function CalendarIcon() {
  return <img src="/images/icons/calendar.svg" width="20" height="20" alt="" aria-hidden="true" />;
}

function to12Hour(hour24) {
  const h = Number.isFinite(hour24) ? hour24 : 0;
  const meridiem = h >= 12 ? 'PM' : 'AM';
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, meridiem };
}

function to24Hour(hour12, meridiem) {
  let h = Number(hour12) % 12;
  if (meridiem === 'PM') h += 12;
  return h;
}

function parseFlexibleDate(input) {
  if (!input || typeof input !== 'string') return null;

  const re = /^(\d{2,4})[-/](\d{1,2})[-/](\d{2,4})(?:(?:T|,\s*|\s+)(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?)?/;
  const m = re.exec(input.trim());
  if (!m) return null;

  let year, month, day;
  if (m[1].length === 4) {
    year = Number(m[1]); month = Number(m[2]) - 1; day = Number(m[3]);
  } else if (m[3].length === 4) {
    month = Number(m[1]) - 1; day = Number(m[2]); year = Number(m[3]);
  } else {
    return null;
  }

  const d = new Date(year, month, day);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;

  const parsed = { year, month, day };
  if (m[4] !== undefined) {
    const minute = Number(m[5]);
    if (minute > 59) return null;
    parsed.hour = m[6] ? to24Hour(Number(m[4]), m[6].toUpperCase()) : Number(m[4]);
    parsed.minute = minute;
  }
  return parsed;
}

function parseIso(iso, showTime) {
  const parsed = parseFlexibleDate(iso);
  if (!parsed) return null;
  if (!showTime) return { year: parsed.year, month: parsed.month, day: parsed.day };
  return {
    year: parsed.year,
    month: parsed.month,
    day: parsed.day,
    hour: parsed.hour ?? 0,
    minute: parsed.minute ?? 0,
  };
}

function normalizeDateOnly(input) {
  if (!input) return input;
  const parsed = parseFlexibleDate(input);
  return parsed ? toIso(parsed.year, parsed.month, parsed.day) : input;
}

function parseTyped(text, showTime) {
  const trimmed = String(text || '').trim();
  if (showTime) {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/.exec(trimmed);
    if (!m) return null;
    const month = Number(m[1]) - 1;
    const day = Number(m[2]);
    const year = Number(m[3]);
    const d = new Date(year, month, day);
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
    const hour = to24Hour(Number(m[4]), m[6].toUpperCase());
    const minute = Number(m[5]);
    if (minute > 59) return null;
    return { year, month, day, hour, minute };
  }

  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (!m) return null;
  const month = Number(m[1]) - 1;
  const day = Number(m[2]);
  const year = Number(m[3]);
  const d = new Date(year, month, day);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return { year, month, day };
}

function toIso(year, month, day, hour, minute, showTime) {
  const datePart = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  if (!showTime) return datePart;
  return `${datePart}T${String(hour ?? 0).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}:00`;
}

function formatDisplay({ year, month, day, hour, minute }, showTime) {
  const datePart = `${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  if (!showTime) return datePart;
  const { hour12, meridiem } = to12Hour(hour ?? 0);
  return `${datePart}, ${String(hour12).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')} ${meridiem}`;
}

function todayParts() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate(), hour: d.getHours(), minute: d.getMinutes() };
}

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default CalendarPicker;
